import { useCallback, useEffect, useState, type FormEvent } from "react";
import { CreditCard, MessageSquareText, Plus, Trash2 } from "lucide-react";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { useCardPayments } from "../hooks/useCardPayments";
import { useAuth } from "../hooks/useAuth";
import { InvoiceSign } from "./InvoiceSign";

// Payment on the job: the job's invoices and what's owed, and the ways to
// collect right there — card on this device (Stripe checkout), a texted pay
// link, or cash/check recorded by hand. A Field Tech gets this for jobs
// assigned to them (the server checks), including a quick invoice when the
// office hasn't made one yet.

interface BillingInvoice {
  id: string;
  number: number;
  status: "draft" | "sent" | "partially_paid" | "paid" | "void" | "written_off";
  totalCents: number;
  amountPaidCents: number;
  remainingCents: number;
  lines: Array<{ description: string; quantity: number; unitPriceCents: number }>;
  signerName: string | null;
  signedAt: number | null;
}
interface Billing {
  invoices: BillingInvoice[];
  payer: { name: string; hasPhone: boolean; hasEmail: boolean } | null;
}

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const STATUS: Record<BillingInvoice["status"], string> = {
  draft: "Draft",
  sent: "Sent",
  partially_paid: "Partly paid",
  paid: "Paid",
  void: "Void",
  written_off: "Written off",
};

type StartLine = { description: string; quantity: number; unitPriceCents: number };

export function JobPayment({ tenantId, jobId, startLines }: { tenantId: string; jobId: string; startLines?: StartLine[] }) {
  const [billing, setBilling] = useState<Billing | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [recording, setRecording] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState<string | null>(null);
  // Card buttons only once the company's own Stripe account can take charges.
  const { ready: cardsReady } = useCardPayments(tenantId);
  const { role } = useAuth();

  const load = useCallback(async () => {
    try {
      setBilling(await call<Billing>("getJobBilling", { tenantId, jobId }, { mutation: false }));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [tenantId, jobId]);
  useEffect(() => void load(), [load]);

  async function act(key: string, fn: () => Promise<unknown>, done?: string) {
    setBusy(key);
    setError("");
    setNote("");
    try {
      await fn();
      if (done) setNote(done);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function payByCard(inv: BillingInvoice) {
    setBusy(`card-${inv.id}`);
    setError("");
    try {
      const res = await call<{ url: string }>("createInvoicePayLink", { tenantId, invoiceId: inv.id }, { mutation: false });
      window.location.href = res.url; // the customer pays right on this device
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  if (!billing) {
    return (
      <Card>
        <CardTitle className="mb-0">Payment</CardTitle>
        <p className="mt-1 text-xs text-steel">{error || "Loading…"}</p>
      </Card>
    );
  }

  const owed = billing.invoices.reduce((s, i) => s + (i.status === "draft" ? 0 : i.remainingCents), 0);
  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Payment</CardTitle>
        <span className="text-xs text-steel">
          {billing.payer ? `Bill to ${billing.payer.name}` : "No customer on this job"}
          {owed > 0 ? ` · ${money(owed)} due` : ""}
        </span>
      </div>

      {billing.invoices.length === 0 && !creating ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs text-steel">No invoice for this job yet.</p>
          <Button className="px-3 py-1 text-xs" onClick={() => setCreating(true)} disabled={!billing.payer}>
            <Plus size={13} /> Create invoice
          </Button>
        </div>
      ) : null}

      {billing.invoices.map((inv) => (
        <div key={inv.id} className="mt-2 border-t border-steel/10 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-ink">
              <span className="font-medium">Invoice #{inv.number}</span> · {money(inv.totalCents)} ·{" "}
              <span className={inv.status === "paid" ? "text-success" : "text-steel"}>{STATUS[inv.status]}</span>
              {inv.status === "partially_paid" ? <span className="text-steel"> · {money(inv.remainingCents)} left</span> : null}
              {inv.signedAt ? <span className="text-steel"> · signed by {inv.signerName}</span> : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {inv.status === "draft" ? (
                <Button
                  className="px-3 py-1 text-xs"
                  busy={busy === `send-${inv.id}`}
                  onClick={() =>
                    void act(`send-${inv.id}`, () => call("setInvoiceStatus", { tenantId, invoiceId: inv.id, action: "send" }), "Invoice finalized — ready to collect.")
                  }
                >
                  Finalize to collect
                </Button>
              ) : null}
              {inv.status === "sent" || inv.status === "partially_paid" ? (
                <>
                  {cardsReady ? (
                    <Button className="px-3 py-1 text-xs" busy={busy === `card-${inv.id}`} onClick={() => void payByCard(inv)}>
                      <CreditCard size={13} /> Pay by card
                    </Button>
                  ) : null}
                  {cardsReady && billing.payer?.hasPhone ? (
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      busy={busy === `text-${inv.id}`}
                      onClick={() =>
                        void act(`text-${inv.id}`, () => call("sendInvoiceText", { tenantId, invoiceId: inv.id }), "Pay link texted to the customer.")
                      }
                    >
                      <MessageSquareText size={13} /> Text pay link
                    </Button>
                  ) : null}
                  <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setRecording(recording === inv.id ? null : inv.id)}>
                    Cash / check
                  </Button>
                </>
              ) : null}
              {inv.status !== "draft" && !inv.signedAt ? (
                <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setSigning(signing === inv.id ? null : inv.id)}>
                  Customer signs
                </Button>
              ) : null}
            </div>
          </div>
          {!cardsReady && (inv.status === "sent" || inv.status === "partially_paid") ? (
            <p className="mt-0.5 text-[11px] text-steel">
              Card payments aren't set up yet{role === "owner" ? " (Setup → Card payments)" : "; the owner sets them up"}. Take cash or check for now.
            </p>
          ) : null}
          {inv.status === "draft" ? (
            <p className="mt-0.5 text-[11px] text-steel">
              {inv.lines.map((l) => `${l.quantity} × ${l.description} @ ${money(l.unitPriceCents)}`).join(" · ")}
            </p>
          ) : null}
          {signing === inv.id ? (
            <InvoiceSign
              tenantId={tenantId}
              invoiceId={inv.id}
              number={inv.number}
              totalCents={inv.totalCents}
              defaultName={billing.payer?.name ?? ""}
              onCancel={() => setSigning(null)}
              onDone={(n) => {
                setSigning(null);
                setNote(`Signed by ${n}.`);
                void load();
              }}
            />
          ) : null}
          {recording === inv.id ? (
            <RecordPayment
              remainingCents={inv.remainingCents}
              busy={busy === `pay-${inv.id}`}
              onCancel={() => setRecording(null)}
              onSave={(amountCents, method, memo) =>
                void act(
                  `pay-${inv.id}`,
                  async () => {
                    await call("recordInvoicePayment", { tenantId, invoiceId: inv.id, amountCents, method, note: memo });
                    setRecording(null);
                  },
                  `${money(amountCents)} ${method} recorded.`,
                )
              }
            />
          ) : null}
        </div>
      ))}

      {creating ? (
        <QuickInvoice
          startLines={startLines}
          busy={busy === "create"}
          onCancel={() => setCreating(false)}
          onSave={(lines, taxRateBps) =>
            void act(
              "create",
              async () => {
                await call("createInvoice", { tenantId, jobId, customerId: null, lines, taxRateBps });
                setCreating(false);
              },
              "Invoice drafted — check it, then finalize to collect.",
            )
          }
        />
      ) : null}

      {note ? <p className="mt-2 text-xs text-success">{note}</p> : null}
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
    </Card>
  );
}

function RecordPayment({
  remainingCents,
  busy,
  onSave,
  onCancel,
}: {
  remainingCents: number;
  busy: boolean;
  onSave: (amountCents: number, method: "cash" | "check" | "card" | "other", note: string) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState((remainingCents / 100).toFixed(2));
  const [method, setMethod] = useState<"cash" | "check" | "card" | "other">("cash");
  const [memo, setMemo] = useState("");
  const cents = Math.round(parseFloat(amount || "0") * 100);
  function submit(e: FormEvent) {
    e.preventDefault();
    if (cents > 0) onSave(cents, method, memo.trim());
  }
  return (
    <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-cream p-2">
      <div className="w-28">
        <Label>Amount</Label>
        <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="w-28">
        <Label>Method</Label>
        <Select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
          <option value="cash">Cash</option>
          <option value="check">Check</option>
          <option value="card">Card (own reader)</option>
          <option value="other">Other</option>
        </Select>
      </div>
      <div className="min-w-32 flex-1">
        <Label>{method === "check" ? "Check #" : "Note"}</Label>
        <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
      </div>
      <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={cents <= 0 || cents > remainingCents}>
        Record {cents > 0 ? money(cents) : ""}
      </Button>
      <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={onCancel}>
        Cancel
      </Button>
      {cents > remainingCents ? <p className="w-full text-[11px] text-danger">More than the {money(remainingCents)} left.</p> : null}
    </form>
  );
}

interface Line {
  description: string;
  quantity: string;
  price: string;
}

function QuickInvoice({
  startLines,
  busy,
  onSave,
  onCancel,
}: {
  /** The job's project item ("Elevation A"): its bid lines and prices to start from. */
  startLines?: StartLine[];
  busy: boolean;
  onSave: (lines: Array<{ description: string; quantity: number; unitPriceCents: number }>, taxRateBps: number) => void;
  onCancel: () => void;
}) {
  const [lines, setLines] = useState<Line[]>(() =>
    startLines?.length
      ? startLines.map((l) => ({ description: l.description, quantity: String(l.quantity), price: (l.unitPriceCents / 100).toFixed(2) }))
      : [{ description: "", quantity: "1", price: "" }],
  );
  const [tax, setTax] = useState("0");
  const parsed = lines
    .map((l) => ({
      description: l.description.trim(),
      quantity: parseFloat(l.quantity || "0"),
      unitPriceCents: Math.round(parseFloat(l.price || "0") * 100),
    }))
    .filter((l) => l.description && l.quantity > 0);
  const subtotal = parsed.reduce((s, l) => s + Math.round(l.quantity * l.unitPriceCents), 0);
  const taxBps = Math.round(parseFloat(tax || "0") * 100);
  const set = (i: number, next: Partial<Line>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...next } : l)));

  function submit(e: FormEvent) {
    e.preventDefault();
    if (parsed.length > 0) onSave(parsed, Math.min(Math.max(taxBps, 0), 5000));
  }
  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-lg bg-cream p-2">
      {lines.map((l, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            {i === 0 ? <Label>Work / item</Label> : null}
            <Input value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="Garage door spring replacement" />
          </div>
          <div className="w-16">
            {i === 0 ? <Label>Qty</Label> : null}
            <Input type="number" min="0" step="any" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          </div>
          <div className="w-24">
            {i === 0 ? <Label>Price</Label> : null}
            <Input type="number" min="0" step="0.01" value={l.price} onChange={(e) => set(i, { price: e.target.value })} />
          </div>
          {lines.length > 1 ? (
            <button type="button" aria-label="Remove line" className="pb-2 text-steel hover:text-danger" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          ) : null}
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setLines((ls) => [...ls, { description: "", quantity: "1", price: "" }])}>
          <Plus size={13} /> Line
        </Button>
        <div className="w-20">
          <Label>Tax %</Label>
          <Input type="number" min="0" max="50" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} />
        </div>
        <span className="flex-1 text-right text-xs text-ink">
          {money(subtotal + Math.round((subtotal * taxBps) / 10000))} total
        </span>
        <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={parsed.length === 0}>
          Draft invoice
        </Button>
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
